
// src/components/Blog.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFirestore, collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import './Blog.css';
import { FaArrowLeft, FaSpinner, FaCalendarAlt, FaClock, FaTag } from 'react-icons/fa';

const Blog = () => {
  const [articles, setArticles] = useState([]);
  const [blogSettings, setBlogSettings] = useState({
    title: 'ZappCart Blog',
    subtitle: 'Fresh insights, recipes, and meat knowledge',
    description: 'Discover the latest news, cooking tips, and meat expertise from ZappCart.',
    headerImage: 'https://images.unsplash.com/photo-1625604086814-358a4ad73165?q=80&w=2070',
    categories: ['Recipes', 'Meat Guide', 'Health & Nutrition', 'Company News']
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Firestore reference
  const db = getFirestore();
  const articlesCollection = collection(db, 'articles');

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        // First get blog settings
        const blogSettingsDoc = await getDoc(doc(db, 'settings', 'blog'));
        if (blogSettingsDoc.exists()) {
          setBlogSettings({
            ...blogSettings,
            ...blogSettingsDoc.data()
          });
        }
        
        // Then get articles sorted by date (newest first)
        const articlesQuery = query(articlesCollection, orderBy('date', 'desc'));
        const querySnapshot = await getDocs(articlesQuery);
        
        const articlesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setArticles(articlesData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching blog data:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchBlogData();
  }, []);

  const formatDate = (dateString) => {
    try {
      // Handle Firestore timestamps or regular date strings
      const date = typeof dateString === 'object' && dateString.toDate 
        ? dateString.toDate() 
        : new Date(dateString);
      
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="blog-container loading-container">
        <div className="blog-header">
          <Link to="/" className="back-link">
            <FaArrowLeft /> Back to Home
          </Link>
        </div>
        <div className="loading-section">
          <div className="loading-spinner">
            <FaSpinner className="spinner-icon" />
          </div>
          <p>Loading blog content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-container error-container">
        <div className="blog-header">
          <Link to="/" className="back-link">
            <FaArrowLeft /> Back to Home
          </Link>
        </div>
        <div className="error-section">
          <h2>Error Loading Content</h2>
          <p>We're having trouble loading the blog content. Please try refreshing the page.</p>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-container">
      <div className="blog-header-nav">
        <Link to="/" className="back-link">
          <FaArrowLeft /> Back to Home
        </Link>
      </div>

      <main className="blog-content">
        {/* Hero Section */}
        <section className="blog-hero">
          <div className="blog-hero-content">
            <h1>{blogSettings.title}</h1>
            <p className="hero-subtitle">{blogSettings.subtitle}</p>
            <p className="hero-description">{blogSettings.description}</p>
          </div>
          <div className="blog-hero-image" style={{ backgroundImage: `url(${blogSettings.headerImage})` }}></div>
        </section>

        {/* Categories */}
        {/* <section className="categories-section">
          <h2>Categories</h2>
          <div className="categories-list">
            {blogSettings.categories.map((category, index) => (
              <div key={index} className="category-tag">
                <FaTag />
                <span>{category}</span>
              </div>
            ))}
          </div>
        </section> */}

        {/* Featured Articles */}
        <section className="featured-articles-section">
          <h2>Featured Articles</h2>
          <div className="articles-grid">
            {articles.map((article) => (
              <article key={article.id} className="article-card">
                <div className="article-image">
                  {article.imageURL ? (
                    <img src={article.imageURL} alt={article.title} />
                  ) : article.image ? (
                    <div className={`article-image-placeholder ${article.image}`}></div>
                  ) : (
                    <div className="article-image-placeholder default"></div>
                  )}
                </div>
                <div className="article-content">
                  <div className="article-category">
                    <FaTag />
                    <span>{article.category}</span>
                  </div>
                  <div className="article-meta">
                    <span className="article-date">
                      <FaCalendarAlt />
                      {formatDate(article.date)}
                    </span>
                    {/* <span className="article-read-time">
                      <FaClock />
                      5 min read
                    </span> */}
                  </div>
                  <h3 className="article-title">{article.title}</h3>
                  <p className="article-excerpt">{article.excerpt}</p>
                  {/* <Link to={`/blog/${article.id}`} className="read-more-btn">
                    Read More
                  </Link> */}
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Newsletter Signup */}
        {/* <section className="newsletter-section">
          <div className="newsletter-content">
            <h2>Stay Updated</h2>
            <p>Get notified when we publish new articles and recipes!</p>
            <div className="newsletter-form">
              <input 
                type="email" 
                placeholder="Enter your email address" 
              />
              <button>
                Subscribe
              </button>
            </div>
          </div>
        </section> */}
      </main>

      <footer className="blog-footer">
        <Link to="/" className="footer-home-link">Return to Main Website</Link>
      </footer>
    </div>
  );
};

export default Blog;